import { Component, computed, inject, Signal, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { ButtonComponent } from '../../components/button/button.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import TYPES from '../../constants/types';
import { Psychologist } from '../../models/psychologists';
import { PsychologistService } from '../../services/psychologist.service';
import { SelectOnePsychologistComponent } from '../../components/select-one-psychologist/select-one-psychologist.component';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { MatIcon } from '@angular/material/icon';
import { UserPsychologist } from '../../models/user-psychologist';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouteReuseStrategy } from '@angular/router';
import { UserPsychologistService } from '../../services/user-psychologist.service';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { LogSharedService } from '../../services/log-shared.service';
import { LogShared } from '../../models/log-shared';
import { CustomRouteReuseStrategy } from '../../util/custom-route-reuse-strategy';
import { EdgeFunctionCallerService } from '../../services/edge-function-caller.service';

@Component({
  selector: 'app-associate-to-psychologist',
  imports: [
    TranslatePipe,
    ButtonComponent,
    DialogModule,
    MatIcon
  ],
  templateUrl: './associate-to-psychologist.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './associate-to-psychologist.component.scss'
})
export class AssociateToPsychologistComponent {
  authService: AuthService = inject(AUTH_SERVICE);
  translate: TranslateService = inject(TranslateService);
  userPsychologistService: UserPsychologistService<UserPsychologist> = inject(UserPsychologistService);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  logSharedService: LogSharedService<LogShared> = inject(LogSharedService);
  edgeFunctionCallerService: EdgeFunctionCallerService = inject(EdgeFunctionCallerService);

  psychologists: WritableSignal<Psychologist[]> = signal([]);
  
  selectablePsychologistList: Signal<Psychologist[]> = computed(() => {
    
    let list: Psychologist[] = [];

    for(let item of this.psychologists()){
      list.push({
        ...item
      });
    }
    return list;
  });

  pyscholosgistSelected: WritableSignal<Psychologist | null> = signal(null);

  dialog: Dialog = inject(Dialog);
  snackBar: MatSnackBar = inject(MatSnackBar);

  BUTTON_TYPES: any;
  
  constructor(
    private router: Router,
    private reuseStrategy: RouteReuseStrategy
  ) {

    this.psychologistService.getAllActivePremiumPsychologists().then((psychologists) => {
      this.psychologists.set(psychologists);
    });

    this.BUTTON_TYPES = TYPES.UI.BUTTON;
  }

  async requestAssociation() {
    if(this.isPsychologistSelected()){
      const userId = this.authService.user()?.id;
      if(userId){      
        const oldAssociation = await this.userPsychologistService.getUserPsychologistByUserId(userId);

        if(oldAssociation){
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
              title: 'update-shared-logs-dialog.alert_title',
              message: 'update-shared-logs-dialog.alert_message'
            },
          });
          
          dialogRef.closed.subscribe(async(result:any)  => {
            if(result?.action === 'continue'){
              const psychologistId = this.pyscholosgistSelected()?.id;
              if(psychologistId){
                await this.logSharedService.updateAllByUserId(userId, psychologistId);

                //Delete the previous cache from historical screen
                const customReuseStrategy = this.reuseStrategy as CustomRouteReuseStrategy;
                customReuseStrategy.clearRoute('home/(home:historical)');

              }
            }
            const oldId = oldAssociation.id;
            if(oldId){
              await this.userPsychologistService.delete(TYPES.DATABASE.TABLES.USERS_PSYCHOLOGISTS, oldId);                            
            }

            await this.userPsychologistService.create(
              TYPES.DATABASE.TABLES.USERS_PSYCHOLOGISTS,
              {
                user_id: this.authService.user()?.id,
                psychologist_id: this.pyscholosgistSelected()?.id,
                status: TYPES.DATABASE.STATUS.WAITING_VALIDATION['code']
              }
            );

            const psychologistUserId = this.pyscholosgistSelected()?.user_id;
            if(psychologistUserId){
              this.edgeFunctionCallerService.addPushNotification(
                psychologistUserId, 
                new Date().toISOString(),
                this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_REQUEST.title),
                this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_REQUEST.body),
                {
                  onActionClick: {
                    default: {operation: "navigateLastFocusedOrOpen", url: "/home"}
                  }
                }
              );
            }

            const message:string = this.translate.instant('info.association_requested');
            const action:string = this.translate.instant('buttons.dismiss');
            this.snackBar.open(message, action);

            this.router.navigate(['/home'], { replaceUrl: true });
          });
        }else{
          await this.userPsychologistService.create(
            TYPES.DATABASE.TABLES.USERS_PSYCHOLOGISTS,
            {
              user_id: this.authService.user()?.id,
              psychologist_id: this.pyscholosgistSelected()?.id,
              status: TYPES.DATABASE.STATUS.WAITING_VALIDATION['code']
            }
          );

          const psychologistUserId = this.pyscholosgistSelected()?.user_id;
          if(psychologistUserId){
            this.edgeFunctionCallerService.addPushNotification(
              psychologistUserId, 
              new Date().toISOString(),
              this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_REQUEST.title),
              this.translate.instant(TYPES.DATABASE.NOTIFICATION.CONNECTION_REQUEST.body),
              {
                onActionClick: {
                  default: {operation: "navigateLastFocusedOrOpen", url: "/home"}
                }
              }
            );
          }

          const message:string = this.translate.instant('info.association_requested');
          const action:string = this.translate.instant('buttons.dismiss');
          this.snackBar.open(message, action);

          this.router.navigate(['/home'], { replaceUrl: true });
        }
      }
    }
  }

  cancel()  {
    history.back();
  }

  isPsychologistSelected(): boolean{
    return this.pyscholosgistSelected() !== null;
  }

  openPsychologistsToSelect(){
    const dialogRef = this.dialog.open(SelectOnePsychologistComponent, {
      disableClose: false,
      data: {
        title: 'associate-to-psychologist.subtitle',
        list: this.selectablePsychologistList(),
      },
    });

    dialogRef.closed.subscribe((result:any)  => {
      if(result && result.selectedItem){
        this.pyscholosgistSelected.set(this.psychologists().find((psychologist) => psychologist.id === result.selectedItem.id) || null);
      }
    });
  }
}
